import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getSettings } from '../../api/companies.api';

interface SidebarProps {
  isOpen: boolean;
  currentPath: string;
  onClose?: () => void;
}

type UserRole = 'SUPERADMIN' | 'ADMIN' | 'OPERADOR';

export default function Sidebar({ isOpen, currentPath, onClose }: SidebarProps) {
  const navigate = useNavigate();
  const storedRole = localStorage.getItem('userRole');
  const userRole: UserRole = (storedRole === 'SUPERADMIN' || storedRole === 'ADMIN' || storedRole === 'OPERADOR') 
    ? storedRole 
    : 'OPERADOR';

  const handleNavigation = (path: string) => {
    navigate(path);
    if (onClose) onClose(); // Cerrar sidebar en móvil después de navegar
  };

  const menuItems = {
    SUPERADMIN: [
      { icon: "ri-dashboard-line", label: "Dashboard", path: "/dashboard" },
      { icon: "ri-building-line", label: "Empresas Clientes", path: "/empresas" },
      { icon: "ri-vip-crown-line", label: "Planes y Suscripciones", path: "/planes" },
      { icon: "ri-file-list-line", label: "Facturación", path: "/facturacion" },
      { icon: "ri-bar-chart-box-line", label: "Reportes Globales", path: "/reportes" },
      { icon: "ri-shield-check-line", label: "Auditoría", path: "/auditoria" },
    ],
    ADMIN: [
      { icon: "ri-dashboard-line", label: "Dashboard", path: "/dashboard" },
      { icon: "ri-car-line", label: "Vehículos", path: "/vehiculos" },
      { icon: "ri-arrow-left-right-line", label: "Movimientos", path: "/movimientos" },
      { icon: "ri-parking-box-line", label: "Espacios", path: "/espacios" },
      { icon: "ri-store-2-line", label: "Tienda", path: "/tienda", requiresModule: "store" },
      { icon: "ri-map-pin-2-line", label: "Sedes", path: "/sedes" },
      { icon: "ri-user-line", label: "Usuarios", path: "/usuarios" },
      { icon: "ri-file-text-line", label: "Contratos", path: "/contratos" },
      { icon: "ri-price-tag-3-line", label: "Tarifas", path: "/tarifas" },
      { icon: "ri-money-dollar-circle-line", label: "Pagos", path: "/pagos" },
      { icon: "ri-ticket-line", label: "Boletos", path: "/boletos" },
      { icon: "ri-time-line", label: "Turnos", path: "/turnos" },
      { icon: "ri-file-list-line", label: "Fact. Electrónicas", path: "/facturas-electronicas" },
      { icon: "ri-money-dollar-circle-line", label: "Gastos", path: "/gastos", requiresModule: "expenses" },
      { icon: "ri-bar-chart-line", label: "Reportes", path: "/reportes" },
      { icon: "ri-shield-check-line", label: "Auditoría", path: "/auditoria" },
      { icon: "ri-settings-3-line", label: "Configuración", path: "/configuracion" },
    ],
    OPERADOR: [
      { icon: "ri-dashboard-line", label: "Dashboard", path: "/dashboard" },
      { icon: "ri-arrow-left-right-line", label: "Movimientos", path: "/movimientos" },
      { icon: "ri-money-dollar-circle-line", label: "Pagos", path: "/pagos" },
      { icon: "ri-ticket-line", label: "Boletos", path: "/boletos" },
      { icon: "ri-time-line", label: "Turnos", path: "/turnos" },
      { icon: "ri-store-2-line", label: "Tienda", path: "/tienda", requiresModule: "store", requiresOperatorPermission: true },
      { icon: "ri-money-dollar-circle-line", label: "Gastos", path: "/gastos", requiresModule: "expenses", requiresOperatorPermission: true },
      { icon: "ri-file-text-line", label: "Contratos", path: "/contratos" },
    ],
  };

  // Obtener settings de la empresa para validar módulos
  const [companySettings, setCompanySettings] = React.useState<any>(null);
  const [settingsLoaded, setSettingsLoaded] = React.useState(false);

  React.useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await getSettings();
        console.log('[DEBUG Sidebar] Company settings loaded:', data);
        setCompanySettings(data);
      } catch (error) {
        // En caso de error (incluyendo 401), usar configuración por defecto
        // Esto permite que el sidebar cargue sin errores, aunque oculte los módulos restringidos
        console.error('Error fetching company settings:', error);
        setCompanySettings({ 
          expensesEnabled: false, 
          expensesOperatorAllowed: false,
          storeEnabled: false,
          storeOperatorAllowed: false
        });
      } finally {
        setSettingsLoaded(true);
      }
    };
    
    if (userRole !== 'SUPERADMIN') {
      fetchSettings();
    } else {
      setSettingsLoaded(true);
    }
  }, [userRole]);

  // Filtrar menú basado en permisos - esperar a que carguen los settings
  const filteredMenu = React.useMemo(() => {
    // No filtrar hasta que settings se hayan cargado (o sea SUPERADMIN)
    if (!settingsLoaded && userRole !== 'SUPERADMIN') {
      return menuItems[userRole].filter((item: any) => !item.requiresModule);
    }

    return menuItems[userRole].filter((item: any) => {
      // Si el ítem requiere un módulo específico
      if (item.requiresModule === 'expenses') {
        // Si el módulo no está habilitado, no mostrar
        if (!companySettings?.expensesEnabled) {
          return false;
        }
        // Si es operador y el ítem requiere permiso de operador
        if (userRole === 'OPERADOR' && item.requiresOperatorPermission) {
          // Verificar que tenga permiso
          return companySettings?.expensesOperatorAllowed === true;
        }
      }
      // Módulo de tienda: lo activa el SUPERADMIN por empresa
      if (item.requiresModule === 'store') {
        if (!companySettings?.storeEnabled) {
          return false;
        }
        if (userRole === 'OPERADOR' && item.requiresOperatorPermission) {
          return companySettings?.storeOperatorAllowed === true;
        }
      }
      return true;
    });
  }, [userRole, companySettings, settingsLoaded]);

  return (
    <>
      {/* Overlay para móvil */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-screen bg-white border-r border-slate-200 transition-all duration-300 z-50 ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } w-64 lg:z-40`}
      >
      <div className="flex flex-col h-full">
        {/* Logo */}
        <div className="h-16 flex items-center justify-center border-b border-slate-200 px-4 sm:px-6">
          <div className="flex items-center gap-2 sm:gap-3">
            <span className="text-lg sm:text-xl font-bold text-slate-800" style={{ fontFamily: '"Pacifico", serif' }}>
              Parqium
            </span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-6 px-4">
          {filteredMenu.map((item, idx) => {
            const isActive = currentPath === item.path;
            return (
              <button
                key={idx}
                onClick={() => handleNavigation(item.path)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition whitespace-nowrap ${
                  isActive
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <i className={`${item.icon} text-lg`}></i>
                <span className="text-sm font-medium">{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Close button - Solo visible en móvil */}
        <button
          onClick={onClose}
          className="lg:hidden absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 transition"
        >
          <i className="ri-close-line text-xl text-slate-600"></i>
        </button>

        {/* User Info */}
        <div className="border-t border-slate-200 p-3 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2">
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-user-line text-base sm:text-lg text-slate-600"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-slate-800 truncate">
                {localStorage.getItem('userName') || 'Usuario'}
              </p>
              <p className="text-[10px] sm:text-xs text-slate-500">{userRole}</p>
            </div>
          </div>
        </div>
      </div>
    </aside>
    </>
  );
}
